import { EVENT_META, COLORS, CHART_COLORS } from "../constants/analyticsConstants";

// ── Formatting helpers ────────────────────────────────────────────────────────

/** "APP_OPENED" → "App opened" */
export function fmtEventName(name = "") {
  return name
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Force a UTC parse regardless of whether the server sends "Z" or not.
 * e.g. "2026-03-20T05:30:00" and "2026-03-20T05:30:00.000000" both become UTC.
 */
function toUTC(ts) {
  if (!ts) return new Date(NaN);
  const s = String(ts).trim();
  // Already has timezone info → trust it
  if (s.endsWith("Z") || s.includes("+") || /[0-9][+-][0-9]{2}:[0-9]{2}$/.test(s)) {
    return new Date(s);
  }
  // No timezone → server is UTC, append Z
  return new Date(s + "Z");
}

/** ISO timestamp → "06:30:00 AM" in IST */
export function fmtTime(ts) {
  return toUTC(ts).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "Asia/Kolkata",
    hour12: true,
  });
}

/** ISO timestamp → "Mar 20" in IST */
export function fmtDate(ts) {
  return toUTC(ts).toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

/** Returns the display color for an event_code */
export function colorForCode(code) {
  return EVENT_META[code]?.color ?? COLORS.gray[3];
}

/** Returns the display name for an event_code */
export function nameForCode(code) {
  return EVENT_META[code]?.name ?? fmtEventName(String(code));
}

// ── Data transformers ─────────────────────────────────────────────────────────

/**
 * Transforms raw event_analytics object into a flat array sorted by count desc.
 * Input:  { APP_OPENED: [{date, count}], ... }
 * Output: [{ name, count, color }, ...]
 */
export function transformEventAnalytics(eventAnalytics = {}) {
  return Object.entries(eventAnalytics)
    .map(([name, series], i) => ({
      name,
      displayName: fmtEventName(name),
      count: series.reduce((sum, d) => sum + d.count, 0),
      series, // keep raw series for sparklines
      color: CHART_COLORS[i % CHART_COLORS.length],
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Transforms raw screen_analytics object into array sorted by views desc.
 * Input:  { "Heart Rate": [{date, views, unique_sessions}], ... }
 * Output: [{ screen, views, sessions, color }, ...]
 */
export function transformScreenAnalytics(screenAnalytics = {}) {
  return Object.entries(screenAnalytics)
    .map(([screen, series], i) => ({
      screen,
      views:    series.reduce((sum, d) => sum + d.views, 0),
      sessions: series.reduce((sum, d) => sum + d.unique_sessions, 0),
      series,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }))
    .sort((a, b) => b.views - a.views);
}

/**
 * Derives KPI summary numbers from the raw API data block.
 */
export function deriveKpis(data) {
  if (!data) return null;

  const totalEvents   = data.events?.total ?? data.events?.items?.length ?? 0;
  const totalSessions = data.user_journey?.length ?? 0;
  const platforms     = [
    ...new Set(
      (data.events?.items ?? [])
        .map((e) => e.device_info?.platform)
        .filter(Boolean)
    ),
  ];

  // Compute rough avg session duration from user_journey
  const durations = (data.user_journey ?? [])
    .filter((s) => s.session_start && s.session_end)
    .map((s) => (new Date(s.session_end) - new Date(s.session_start)) / 60000);
  const avgSessionMin =
    durations.length > 0
      ? +(durations.reduce((a, b) => a + b, 0) / durations.length).toFixed(1)
      : null;

  const uniqueScreens = Object.keys(data.screen_analytics ?? {}).length;

  return {
    totalEvents,
    totalSessions,
    avgSessionMin,
    uniqueScreens,
    platforms,
  };
}

/**
 * Source breakdown counts from events items.
 * Returns [{ source, count, pct }]
 */
export function deriveSourceBreakdown(items = []) {
  const counts = items.reduce((acc, ev) => {
    acc[ev.source] = (acc[ev.source] ?? 0) + 1;
    return acc;
  }, {});
  const total = items.length || 1;
  return Object.entries(counts).map(([source, count]) => ({
    source,
    count,
    pct: Math.round((count / total) * 100),
  }));
}